import { useCallback, useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { jsPDF } from 'jspdf';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { toast } from 'sonner';
import { Share2, Copy, FileImage, FileText, Loader2, Smartphone, Headset } from 'lucide-react';
import { loadConfig } from '@/lib/config';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { loadImage, canvasToBlob, downloadBlob, fileNameSafe, copyCanvasToClipboard } from '@/lib/shareExport';
import casperLogo from '@/assets/casperLogoIcon.png';
import cilLogo from '@/assets/CILLogo.png';
import ciscoLogoWhite from '@/assets/CiscoLogoWhite.svg';

interface Props {
  roomId: string;
  rotated?: boolean;
  /** Rendered inside a modal instead of owning the whole viewport — fills its container's height instead of assuming 100vh. */
  embedded?: boolean;
}

// Single source of truth for both panels so they can never drift apart —
// the qrcode library sets its own inline width/height on the canvas
// (higher CSS specificity than any class), so that has to be overridden
// the same way the marker <img> is sized, not via a Tailwind class.
const PANEL_SIZE = 420;

// Fixed 16:9 canvas the share/export image is rendered at — independent of
// the actual on-screen layout, so the exported file always looks the same
// regardless of window size.
const EXPORT_W = 1600;
const EXPORT_H = 900;

// Canvas has no built-in word-wrap.
function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  cx: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
) {
  const words = text.split(' ');
  let line = '';
  const lines: string[] = [];
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  lines.forEach((l, i) => ctx.fillText(l, cx, y + i * lineHeight));
}

function fillRoundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(x, y, w, h, r);
  } else {
    ctx.rect(x, y, w, h);
  }
  ctx.fill();
}

type AudienceMode = 'mobile' | 'headset';

export function AudiencePage({ roomId, rotated, embedded }: Props) {
  const qrRef = useRef<HTMLCanvasElement | null>(null);
  const [displayName, setDisplayName] = useState<string>('');
  const [markerURL, setMarkerURL] = useState<string>('');
  const [joinURL, setJoinURL] = useState<string>('');
  const [xrURL, setXrURL] = useState<string>('');
  const [joinCode, setJoinCode] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  // Which device type this projector screen is currently guiding — a
  // mobile audience wants the QR+marker two-step, a headset audience
  // wants the join code in giant text instead (no QR: scanning one from
  // inside a headset that's already on your face doesn't work well in
  // practice). Presenter picks with the toggle below; defaults to mobile
  // since that's the more common audience.
  const [mode, setMode] = useState<AudienceMode>('mobile');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cfg = await loadConfig();
      const params = new URLSearchParams({ room: roomId });
      if (rotated) params.set('rotation', 'vertical');
      const url = `${cfg.syncBaseURL}/client.html?${params.toString()}`;
      if (cancelled) return;
      setJoinURL(url);
      setXrURL(`${cfg.syncBaseURL}/xr.html?room=${roomId}`);
      setMarkerURL(`${cfg.syncBaseURL}/assets/markers/CiscoARMarker.png`);
      if (qrRef.current) {
        await QRCode.toCanvas(qrRef.current, url, {
          width: 640,
          margin: 2,
          color: { dark: '#0b1220', light: '#ffffff' },
          errorCorrectionLevel: 'H',
        });
        // toCanvas sets its own inline width/height (matching the `width`
        // option above) — override after the fact so the displayed size
        // matches the marker panel regardless of the render resolution.
        qrRef.current.style.width = `${PANEL_SIZE}px`;
        qrRef.current.style.height = `${PANEL_SIZE}px`;
      }
      try {
        const rooms = await api.listRooms();
        const match = rooms.find((r) => r.id === roomId);
        if (!cancelled && match) {
          setDisplayName(match.name);
          setJoinCode(match.joinCode ?? null);
        }
      } catch { /* audience view tolerates a missing name/code */ }
    })();
    return () => { cancelled = true; };
  }, [roomId, rotated]);

  // Purpose-built canvas render for sharing, rather than a generic
  // DOM-to-image screenshot library — every element on this page is
  // already a known image/canvas/string, so composing it directly avoids
  // the font/cross-origin/sizing flakiness those libraries are prone to.
  const buildShareCanvas = useCallback(async (): Promise<HTMLCanvasElement> => {
    const canvas = document.createElement('canvas');
    canvas.width = EXPORT_W;
    canvas.height = EXPORT_H;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas is not supported in this browser');

    ctx.fillStyle = '#020617'; // slate-950
    ctx.fillRect(0, 0, EXPORT_W, EXPORT_H);

    // The marker PNG is served by the sync server (a different origin) —
    // needs an actual CORS-mode fetch (crossOrigin='anonymous') or
    // drawImage taints the canvas and toBlob/toDataURL throw. The three
    // logos are bundled same-origin assets, so no CORS handling needed.
    const [ciscoImg, cilImg, casperImg, markerImg] = await Promise.all([
      loadImage(ciscoLogoWhite),
      loadImage(cilLogo),
      loadImage(casperLogo),
      markerURL ? loadImage(markerURL, 'anonymous') : Promise.resolve(null),
    ]);

    // Header
    const headerY = 44;
    const logoH = 56;
    let x = 60;
    const drawLogo = (img: HTMLImageElement, whiteBg = false) => {
      const w = (img.width / img.height) * logoH;
      if (whiteBg) {
        ctx.fillStyle = '#ffffff';
        fillRoundedRect(ctx, x - 10, headerY - 10, w + 20, logoH + 20, 8);
      }
      ctx.drawImage(img, x, headerY, w, logoH);
      x += w + 32;
    };
    drawLogo(ciscoImg);
    drawLogo(cilImg, true);
    drawLogo(casperImg);

    ctx.textAlign = 'left';
    ctx.fillStyle = '#f1f5f9'; // slate-100
    ctx.font = '600 24px system-ui, sans-serif';
    ctx.fillText('cAspeR', x, headerY + 24);
    ctx.fillStyle = '#94a3b8'; // slate-400
    ctx.font = '600 11px system-ui, sans-serif';
    ctx.fillText('CONTROL UI', x, headerY + 42);

    ctx.textAlign = 'right';
    ctx.fillStyle = '#94a3b8'; // slate-400
    ctx.font = '600 13px system-ui, sans-serif';
    ctx.fillText('ROOM', EXPORT_W - 60, headerY + 10);
    ctx.fillStyle = '#f1f5f9'; // slate-100
    ctx.font = '600 30px system-ui, sans-serif';
    ctx.fillText(displayName || '—', EXPORT_W - 60, headerY + 46);

    // Two panels: QR code + marker
    const panelSize = 340;
    const panelY = 200;
    const col1CX = EXPORT_W * 0.27;
    const col2CX = EXPORT_W * 0.73;

    const drawPanel = (
      cx: number,
      img: HTMLImageElement | HTMLCanvasElement,
      step: string,
      title: string,
      desc: string,
    ) => {
      const px = cx - panelSize / 2;
      const pad = 22;
      ctx.fillStyle = '#ffffff';
      fillRoundedRect(ctx, px - pad, panelY - pad, panelSize + pad * 2, panelSize + pad * 2, 24);
      ctx.drawImage(img, px, panelY, panelSize, panelSize);

      ctx.textAlign = 'center';
      ctx.fillStyle = '#94a3b8';
      ctx.font = '600 14px system-ui, sans-serif';
      ctx.fillText(step.toUpperCase(), cx, panelY + panelSize + 66);
      ctx.fillStyle = '#f1f5f9';
      ctx.font = '700 28px system-ui, sans-serif';
      ctx.fillText(title, cx, panelY + panelSize + 102);
      ctx.fillStyle = '#94a3b8';
      ctx.font = '400 15px system-ui, sans-serif';
      wrapText(ctx, desc, cx, panelY + panelSize + 134, 400, 21);
    };

    if (mode === 'mobile') {
      if (qrRef.current) {
        drawPanel(
          col1CX,
          qrRef.current,
          'Step 1',
          'Scan to join',
          'Use your phone camera to scan this QR code and open the AR client in your browser.',
        );
      }
      if (markerImg) {
        drawPanel(
          col2CX,
          markerImg,
          'Step 2',
          'Point at the marker',
          'Aim your device at this image to make the model appear in AR.',
        );
      }
    } else {
      // Headset mode: one big centered join-code panel — no QR (scanning
      // one from inside a headset that's already on your face doesn't
      // work well in practice), no marker (passthrough AR places freely).
      const cx = EXPORT_W / 2;
      const panelW = 460;
      const panelH = 340;
      const px = cx - panelW / 2;
      ctx.fillStyle = '#ffffff';
      fillRoundedRect(ctx, px, panelY, panelW, panelH, 24);
      ctx.textAlign = 'center';
      ctx.fillStyle = '#94a3b8';
      ctx.font = '600 16px system-ui, sans-serif';
      ctx.fillText('JOIN CODE', cx, panelY + 70);
      ctx.fillStyle = '#0b1220';
      ctx.font = '700 120px ui-monospace, monospace';
      ctx.fillText(joinCode || '—', cx, panelY + 190);
      ctx.fillStyle = '#94a3b8';
      ctx.font = '400 15px system-ui, sans-serif';
      ctx.fillText('Enter this on the headset’s keypad — no URL needed', cx, panelY + 270);

      ctx.fillStyle = '#f1f5f9';
      ctx.font = '700 28px system-ui, sans-serif';
      ctx.fillText('Enter the code on your headset', cx, panelY + panelH + 66);
      ctx.fillStyle = '#94a3b8';
      ctx.font = '400 15px system-ui, sans-serif';
      wrapText(
        ctx,
        'Open the XR client in your headset’s browser and type this code — passthrough AR places the model automatically.',
        cx,
        panelY + panelH + 98,
        520,
        21,
      );
    }

    // Footer
    ctx.textAlign = 'left';
    ctx.fillStyle = '#64748b'; // slate-500
    ctx.font = '400 14px system-ui, sans-serif';
    ctx.fillText('cAspeR · Cisco Immersive Lab', 60, EXPORT_H - 40);
    ctx.textAlign = 'right';
    ctx.font = '400 13px ui-monospace, monospace';
    ctx.fillText(mode === 'mobile' ? joinURL : xrURL, EXPORT_W - 60, EXPORT_H - 40);

    return canvas;
  }, [displayName, markerURL, joinURL, xrURL, joinCode, mode]);

  function fileNameBase() {
    return `casper-${fileNameSafe(displayName || 'room', 'room')}`;
  }

  const withExporting = useCallback(async (fn: () => Promise<void>) => {
    setExporting(true);
    try {
      await fn();
    } catch (err) {
      toast.error('Export failed', {
        description: err instanceof Error ? err.message : 'Something went wrong generating the image.',
      });
    } finally {
      setExporting(false);
    }
  }, []);

  const handleCopy = () => withExporting(async () => {
    const canvas = await buildShareCanvas();
    await copyCanvasToClipboard(canvas);
    toast.success('Copied to clipboard', { description: 'Paste it into Slack, email, or wherever you need it.' });
  });

  const handleDownloadJPEG = () => withExporting(async () => {
    const canvas = await buildShareCanvas();
    const blob = await canvasToBlob(canvas, 'image/jpeg', 0.92);
    downloadBlob(blob, `${fileNameBase()}.jpg`);
  });

  const handleDownloadPDF = () => withExporting(async () => {
    const canvas = await buildShareCanvas();
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: [canvas.width, canvas.height] });
    pdf.addImage(dataUrl, 'JPEG', 0, 0, canvas.width, canvas.height);
    pdf.save(`${fileNameBase()}.pdf`);
  });

  return (
    <div
      className={cn(
        'group relative grid grid-rows-[auto_1fr_auto] bg-slate-950 text-slate-100',
        embedded ? 'h-full' : 'min-h-screen',
      )}
    >
      {/* Presenter-only control — invisible until hovered/focused so it
          never shows up on the actual projected/audience-facing image.
          Anchored well above the footer (not bottom-6) so it never sits
          on top of the join URL printed there. */}
      <div className="fixed bottom-20 right-6 z-50 flex items-center gap-2 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
        <div className="flex rounded-lg border border-white/15 bg-white/10 p-1 backdrop-blur">
          <button
            type="button"
            onClick={() => setMode('mobile')}
            className={cn(
              'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              mode === 'mobile' ? 'bg-white/20 text-slate-100' : 'text-slate-400 hover:text-slate-100',
            )}
          >
            <Smartphone className="h-3.5 w-3.5" /> Mobile
          </button>
          <button
            type="button"
            onClick={() => setMode('headset')}
            className={cn(
              'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              mode === 'headset' ? 'bg-white/20 text-slate-100' : 'text-slate-400 hover:text-slate-100',
            )}
          >
            <Headset className="h-3.5 w-3.5" /> Headset
          </button>
        </div>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              disabled={exporting}
              className={cn(
                'inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/10 px-4 py-2.5',
                'text-sm font-medium text-slate-100 shadow-lg backdrop-blur transition-colors hover:bg-white/20',
                'disabled:pointer-events-none disabled:opacity-60',
              )}
            >
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
              Share
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              side="top"
              sideOffset={8}
              className="z-50 min-w-[14rem] rounded-md border border-white/10 bg-slate-900 p-1 text-slate-100 shadow-xl"
            >
              <DropdownMenu.Item
                onSelect={handleCopy}
                className="flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 text-sm outline-none focus:bg-white/10"
              >
                <Copy className="h-3.5 w-3.5" />
                Copy to clipboard
              </DropdownMenu.Item>
              <DropdownMenu.Item
                onSelect={handleDownloadJPEG}
                className="flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 text-sm outline-none focus:bg-white/10"
              >
                <FileImage className="h-3.5 w-3.5" />
                Download as JPEG
              </DropdownMenu.Item>
              <DropdownMenu.Item
                onSelect={handleDownloadPDF}
                className="flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 text-sm outline-none focus:bg-white/10"
              >
                <FileText className="h-3.5 w-3.5" />
                Download as PDF
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>

      <header className="flex items-center justify-between px-10 py-6">
        <div className="flex items-center gap-4">
          <img src={ciscoLogoWhite} alt="Cisco" className="h-16 w-auto" />
          <div className="h-10 w-px bg-white/15" />
          <img
            src={cilLogo}
            alt="Cisco Innovation Labs"
            className="h-16 w-auto rounded-md bg-white p-2"
          />
          <img src={casperLogo} alt="cAspeR" className="h-16 w-auto" />
          <div className="flex flex-col leading-none">
            <span className="text-xl font-semibold tracking-tight text-slate-100">cAspeR</span>
            <span className="text-[10px] uppercase tracking-widest text-slate-400">
              Control UI
            </span>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-[0.24em] text-slate-400">Room</p>
          <p className="text-2xl font-semibold tracking-tight">
            {displayName || '—'}
          </p>
        </div>
      </header>

      {mode === 'mobile' ? (
        <main className="grid grid-cols-1 items-start gap-10 px-10 lg:grid-cols-2">
          <section className="flex flex-col items-center gap-5">
            <div className="rounded-3xl bg-white p-6 shadow-2xl">
              <canvas ref={qrRef} aria-label="QR code to join room" />
            </div>
            <div className="flex w-full max-w-md flex-col items-center text-center">
              <p className="text-xs uppercase tracking-[0.24em] text-slate-400">Step 1</p>
              <p className="mt-1 text-3xl font-semibold">Scan to join</p>
              <p className="mt-2 min-h-[40px] text-sm text-slate-400">
                Use your phone camera to scan this QR code and open the AR client
                in your browser.
              </p>
            </div>
          </section>

          <section className="flex flex-col items-center gap-5">
            <div className="rounded-3xl bg-white p-6 shadow-2xl">
              {markerURL && (
                <img
                  src={markerURL}
                  alt="Cisco AR marker"
                  className="object-contain"
                  style={{ width: PANEL_SIZE, height: PANEL_SIZE }}
                />
              )}
            </div>
            <div className="flex w-full max-w-md flex-col items-center text-center">
              <p className="text-xs uppercase tracking-[0.24em] text-slate-400">Step 2</p>
              <p className="mt-1 text-3xl font-semibold">Point at the marker</p>
              <p className="mt-2 min-h-[40px] text-sm text-slate-400">
                Aim your device at this image to make the model appear in AR.
              </p>
            </div>
          </section>
        </main>
      ) : (
        <main className="grid grid-cols-1 items-center justify-center px-10">
          <section className="flex flex-col items-center gap-5">
            <div className="flex flex-col items-center gap-2 rounded-3xl bg-white px-16 py-12 shadow-2xl">
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-slate-400">
                Join code
              </p>
              <p className="font-mono text-8xl font-bold tracking-widest text-slate-900">
                {joinCode || '—'}
              </p>
              <p className="mt-2 text-sm text-slate-500">
                Enter this on the headset&apos;s keypad — no URL needed
              </p>
            </div>
            <div className="flex w-full max-w-lg flex-col items-center text-center">
              <p className="text-xs uppercase tracking-[0.24em] text-slate-400">Quest 3 / Vision Pro</p>
              <p className="mt-1 text-3xl font-semibold">Enter the code on your headset</p>
              <p className="mt-2 min-h-[40px] text-sm text-slate-400">
                Open the XR client in your headset&apos;s browser and type this code —
                passthrough AR places the model automatically, no marker needed.
              </p>
            </div>
          </section>
        </main>
      )}

      <footer className="flex items-center justify-between px-10 py-5 text-xs text-slate-500">
        <span>cAspeR &middot; Cisco Immersive Lab</span>
        <span className="truncate pl-6 font-mono">{mode === 'mobile' ? joinURL : xrURL}</span>
      </footer>
    </div>
  );
}
